/**
 * SNOMED CT — Terminologie clinique globale (v7.0 placeholder)
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dna, ExternalLink, Sparkles } from "lucide-react";

export const Route = createFileRoute("/referential/snomed")({
  component: SNOMEDPage,
});

function SNOMEDPage() {
  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Dna className="h-5 w-5" /> SNOMED CT
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Systematized Nomenclature of Medicine — Clinical Terms
        </p>
      </div>

      <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-4 flex items-start gap-2 text-sm">
        <Sparkles className="h-4 w-4 text-indigo-400 mt-0.5" />
        <div>
          <strong>Disponible en v7.0</strong> · Intégration complète du référentiel international SNOMED CT (350 000 concepts).
          Pour l'instant, le moteur utilise la terminologie CIM-10. SNOMED sera utilisé en parallèle pour la recherche
          par similarité sémantique et l'interopérabilité avec les systèmes étrangers.
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pourquoi SNOMED CT ?</CardTitle>
          <CardDescription>Avantages par rapport à CIM-10</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Feature
            title="Granularité"
            description="CIM-10 a ~14 400 codes, SNOMED CT en a 350 000. Permet une description fine du concept clinique."
          />
          <Feature
            title="Relations sémantiques"
            description="SNOMED CT structure les concepts en hiérarchie : 'Infarctus du myocarde' est-un 'Maladie cardiaque' est-un 'Maladie cardiovasculaire'."
          />
          <Feature
            title="Multi-axial"
            description="SNOMED CT combine diagnostics + procédures + anatomie + morphologie en un seul référentiel."
          />
          <Feature
            title="Recherche par similarité"
            description="Permet de retrouver des cas similaires par proximité sémantique (ex: 'angor' et 'angine de poitrine' sont liés)."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Mapping CIM-10 → SNOMED CT</CardTitle>
          <CardDescription>Exemples de codes pré-mappés pour v7.0</CardDescription>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-2 text-muted-foreground text-xs">CIM-10</th>
                <th className="text-left py-2 text-muted-foreground text-xs">SNOMED CT</th>
                <th className="text-left py-2 text-muted-foreground text-xs">Libellé</th>
              </tr>
            </thead>
            <tbody>
              <Mapping cim10="I10" snomed="59621000" label="Hypertension artérielle essentielle" />
              <Mapping cim10="E11.9" snomed="44054006" label="Diabète sucré de type 2" />
              <Mapping cim10="I20.9" snomed="194828000" label="Angine de poitrine" />
              <Mapping cim10="J45.9" snomed="195967001" label="Asthme" />
              <Mapping cim10="R51" snomed="25064002" label="Céphalée" />
              <Mapping cim10="K21.9" snomed="235595003" label="Reflux gastro-œsophagien" />
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end">
        <Button variant="outline" asChild>
          <a href="https://browser.ihtsdotools.org" target="_blank" rel="noreferrer">
            SNOMED CT Browser <ExternalLink className="h-3 w-3 ml-1" />
          </a>
        </Button>
      </div>
    </div>
  );
}

function Feature({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-md border border-white/5 bg-white/[0.02] p-3">
      <div className="font-medium">{title}</div>
      <div className="text-muted-foreground">{description}</div>
    </div>
  );
}

function Mapping({ cim10, snomed, label }: { cim10: string; snomed: string; label: string }) {
  return (
    <tr className="border-b border-white/5">
      <td className="py-1.5 font-mono text-xs">{cim10}</td>
      <td className="py-1.5 font-mono text-xs">{snomed}</td>
      <td className="py-1.5">{label}</td>
    </tr>
  );
}
